import { Dayjs } from 'dayjs';
import { FC, useCallback, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import TextButton from '@/components/elements/Button/TextButton';
import { GoodFormRacer, RaceProgram } from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { Campaign } from '@/lib/microcms/model';
import { findCampaign, orderByOrderDeadline } from '@/lib/race/singlerace';
import { DayType, RecentRacePrograms } from '@/pages/Top/types';

import GoodRacer from './GoodRacer';
import RaceBox from './RaceBox';
import {
  ButtonTextWrapper,
  CurrentRaceHeadLine,
  DateSelector,
  EmptyMessage,
  GoodWinAvgNote,
  GoodWinAvgTitle,
  GoodWinStreakTitle,
  NextIcon,
  PrevIcon,
  RaceEmptyMessage,
  RaceList,
} from './style';

interface SingleraceListProps {
  recentRacePrograms?: RecentRacePrograms;
  goodWinStreakRacers: GoodFormRacer[];
  goodWinAverageRacers: GoodFormRacer[];
  campaigns: Campaign[];
}

const SingleraceList: FC<SingleraceListProps> = ({
  recentRacePrograms,
  goodWinStreakRacers,
  goodWinAverageRacers,
  campaigns,
}) => {
  const [date, setDate] = useState<Dayjs>(dayjst());
  const [dayType, setDayType] = useState<DayType>(DayType.Today);

  const handleClick = (type: DayType, _date: Dayjs) => {
    if (dayType === DayType.PreviousDay || dayType === DayType.NextDay) {
      setDayType(DayType.Today);
    } else {
      setDayType(type);
    }
    setDate(_date);
  };

  const getCampaign = useCallback(
    (rp: RaceProgram) => findCampaign(rp, campaigns),
    [campaigns],
  );

  const getRaceProgramData = (rpid: string) => {
    if (recentRacePrograms) {
      const targetRp = recentRacePrograms.today.filter(
        today => today.id === rpid,
      )[0];

      return targetRp;
    }
  };

  const getRaceData = (rpid: string, raceid: string) => {
    if (recentRacePrograms) {
      const targetRp = recentRacePrograms.today.filter(
        today => today.id === rpid,
      )[0];
      const targetRace = targetRp.races.filter(r => r.id === raceid)[0];

      return targetRace;
    }
  };

  const sortedGoodWinStreak = useMemo(() => {
    const tempBd: GoodFormRacer[] = [];
    const tempAd: GoodFormRacer[] = [];
    if (goodWinStreakRacers) {
      goodWinStreakRacers.map(racer => {
        const targetRace = getRaceData(racer.raceProgramId, racer.raceId);
        if (targetRace) {
          if (dayjst().isBefore(targetRace.orderDeadline)) {
            tempBd.push(racer);
          } else {
            tempAd.push(racer);
          }
        }
      });
    }
    return tempBd.concat(tempAd);
  }, [goodWinStreakRacers]);

  const sortedGoodWinAverage = useMemo(() => {
    const tempBd: GoodFormRacer[] = [];
    const tempAd: GoodFormRacer[] = [];
    if (goodWinAverageRacers) {
      goodWinAverageRacers.map(racer => {
        const targetRace = getRaceData(racer.raceProgramId, racer.raceId);
        if (targetRace) {
          if (dayjst().isBefore(targetRace.orderDeadline)) {
            tempBd.push(racer);
          } else {
            tempAd.push(racer);
          }
        }
      });
    }
    return tempBd.concat(tempAd);
  }, [goodWinAverageRacers]);

  return (
    <div>
      <DateSelector>
        {dayType !== DayType.PreviousDay ? (
          <TextButton
            size={['s', 's', 'm', 'm']}
            hoverEffect="highlight"
            onClick={() =>
              handleClick(DayType.PreviousDay, date.subtract(1, 'day'))
            }
          >
            <ButtonTextWrapper>
              <PrevIcon />
              {dayType === DayType.NextDay ? '開催中' : '前日'}
            </ButtonTextWrapper>
          </TextButton>
        ) : (
          <div />
        )}
        <CurrentRaceHeadLine>
          <h2>
            {dayType === DayType.PreviousDay && '前日'}
            {dayType === DayType.NextDay && '翌日'}
            {dayType === DayType.Today && '開催中'}
            のレース
          </h2>
          <time>{date.format('YYYY年M月D日')}</time>
        </CurrentRaceHeadLine>
        {dayType !== DayType.NextDay ? (
          <TextButton
            size={['s', 's', 'm', 'm']}
            hoverEffect="highlight"
            onClick={() => handleClick(DayType.NextDay, date.add(1, 'day'))}
          >
            <ButtonTextWrapper>
              {dayType === DayType.PreviousDay ? '開催中' : '翌日'}
              <NextIcon />
            </ButtonTextWrapper>
          </TextButton>
        ) : (
          <div />
        )}
      </DateSelector>
      {recentRacePrograms && recentRacePrograms[dayType].length > 0 ? (
        <RaceList>
          {orderByOrderDeadline(recentRacePrograms[dayType]).map(rp => (
            <CSSTransition key={rp.id} timeout={200}>
              <RaceBox raceProgram={rp} campaign={getCampaign(rp)} />
            </CSSTransition>
          ))}
        </RaceList>
      ) : (
        <RaceEmptyMessage>開催がありません</RaceEmptyMessage>
      )}
      <CurrentRaceHeadLine>
        <GoodWinStreakTitle>連勝中の選手</GoodWinStreakTitle>
        {sortedGoodWinStreak && sortedGoodWinStreak.length > 0 ? (
          <RaceList>
            {sortedGoodWinStreak.map(racer => (
              <CSSTransition key={racer.name} timeout={200}>
                <GoodRacer
                  mode={'WinStreak'}
                  goodFormRacer={racer}
                  raceProgram={getRaceProgramData(racer.raceProgramId)}
                  race={getRaceData(racer.raceProgramId, racer.raceId)}
                />
              </CSSTransition>
            ))}
          </RaceList>
        ) : (
          <EmptyMessage>本日は4連勝以上の選手の出走はありません</EmptyMessage>
        )}
      </CurrentRaceHeadLine>
      <CurrentRaceHeadLine>
        <GoodWinAvgTitle>勝率の高い選手</GoodWinAvgTitle>
        <GoodWinAvgNote>直近15レースの勝率が80%以上</GoodWinAvgNote>
        {sortedGoodWinAverage && sortedGoodWinAverage.length > 0 ? (
          <RaceList>
            {sortedGoodWinAverage.map(racer => (
              <CSSTransition key={racer.name} timeout={200}>
                <GoodRacer
                  mode={'WinAverage'}
                  goodFormRacer={racer}
                  raceProgram={getRaceProgramData(racer.raceProgramId)}
                  race={getRaceData(racer.raceProgramId, racer.raceId)}
                />
              </CSSTransition>
            ))}
          </RaceList>
        ) : (
          <EmptyMessage>
            本日は勝率が80%以上の選手の出走はありません
          </EmptyMessage>
        )}
      </CurrentRaceHeadLine>
    </div>
  );
};

export default SingleraceList;
